import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, ArrowLeft, Target, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, XCircle, BarChart2, ChevronRight, Trash2, Save, Eye
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DEFAULT_CATEGORIES = [
  { category: 'Revenue', budgeted_amount: '' },
  { category: 'Inventory Purchase', budgeted_amount: '' },
  { category: 'Marketing', budgeted_amount: '' },
  { category: 'Salaries', budgeted_amount: '' },
  { category: 'Rent', budgeted_amount: '' },
  { category: 'Utilities', budgeted_amount: '' },
  { category: 'Transport', budgeted_amount: '' },
  { category: 'Other Expense', budgeted_amount: '' },
];

const getHealthBadge = (health) => {
  if (health === 'On Track') return { text: 'On Track', color: '#16a34a', bg: '#dcfce7' };
  if (health === 'Warning') return { text: 'Warning', color: '#ca8a04', bg: '#fef9c3' };
  return { text: 'Over Budget', color: '#dc2626', bg: '#fee2e2' };
};

const StatusBar = ({ percent }) => {
  const p = Math.min(Math.max(percent, 0), 100);
  let color = '#16a34a';
  if (percent > 80) color = '#ca8a04';
  if (percent > 100) color = '#dc2626';
  return (
    <div style={{ width: '100%', height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
    </div>
  );
};

const Budget = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [view, setView] = useState('list'); // list | create | detail
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Create form
  const [budgetName, setBudgetName] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [lineItems, setLineItems] = useState(DEFAULT_CATEGORIES.map(c => ({ ...c })));

  // Detail view
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [advisorData, setAdvisorData] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    setLoading(true);
    try {
      const r = await api.get('/budget');
      setBudgets(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    try {
      const [detailRes, summaryRes] = await Promise.all([
        api.get(`/budget/${id}`),
        api.get(`/budget/${id}/summary`),
      ]);
      setDetailData(detailRes.data);
      setSummaryData(summaryRes.data);
    } catch (e) { console.error(e); }
  };

  const handleCreate = async (activate = false) => {
    if (!budgetName.trim()) { alert('Please enter a budget name.'); return; }
    const items = lineItems.filter(i => i.budgeted_amount !== '' && parseFloat(i.budgeted_amount) >= 0);
    if (items.length === 0) { alert('Please add at least one line item.'); return; }

    setSaving(true);
    try {
      await api.post('/budget', {
        name: budgetName,
        month,
        year,
        status: activate ? 'active' : 'draft',
        line_items: items.map(i => ({
          category: i.category,
          budgeted_amount: parseFloat(i.budgeted_amount || 0),
          notes: i.notes || null,
        })),
      });
      setBudgetName('');
      setMonth(new Date().getMonth() + 1);
      setYear(new Date().getFullYear());
      setLineItems(DEFAULT_CATEGORIES.map(c => ({ ...c })));
      setView('list');
      fetchBudgets();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create budget');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this budget?')) return;
    try {
      await api.delete(`/budget/${id}`);
      fetchBudgets();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete budget');
    }
  };

  const openDetail = async (budget) => {
    setSelectedBudget(budget);
    setView('detail');
    setAdvisorData(null);
    await fetchDetail(budget.id);
  };

  const fetchAdvisor = async (id) => {
    setAdvisorLoading(true);
    try {
      const r = await api.post(`/budget/${id}/analyze`);
      setAdvisorData(r.data);
    } catch (e) { console.error(e); }
    setAdvisorLoading(false);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const addCategory = () => {
    setLineItems([...lineItems, { category: '', budgeted_amount: '' }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // ==================== LIST VIEW ====================
  const renderList = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1e293b' }}>Budget Planner</h1>
        <button
          onClick={() => setView('create')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 10, border: 'none',
            background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer'
          }}
        >
          <Plus size={16} /> New Budget
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading budgets...</div>
      ) : budgets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b', background: '#f8fafc', borderRadius: 12 }}>
          <Target size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>No budgets created yet.</p>
          <button onClick={() => setView('create')} style={{ marginTop: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
            Create your first budget
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Month/Year</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Total Budgeted</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Total Actual</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Variance</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#475569' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#475569' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {budgets.map(b => {
                const isOver = b.total_variance > 0;
                return (
                  <tr key={b.id} style={{ borderTop: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => openDetail(b)}>
                    <td style={{ padding: '12px 16px', color: '#1e293b' }}>{MONTHS[b.month - 1]} {b.year}</td>
                    <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 500 }}>{b.name}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#1e293b' }}>{fmtRs(b.total_budgeted)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#1e293b' }}>{fmtRs(b.total_actual)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: isOver ? '#dc2626' : '#16a34a', fontWeight: 500 }}>
                      {isOver ? '+' : ''}{fmtRs(b.total_variance)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                        background: b.status === 'active' ? '#dcfce7' : '#f1f5f9',
                        color: b.status === 'active' ? '#16a34a' : '#64748b',
                      }}>
                        {b.status === 'active' ? 'Active' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); openDetail(b); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', marginRight: 8 }}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(b.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ==================== CREATE VIEW ====================
  const renderCreate = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1e293b' }}>New Budget</h1>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, maxWidth: 800 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Budget Name</label>
            <input
              value={budgetName}
              onChange={e => setBudgetName(e.target.value)}
              placeholder="e.g. May 2026 Budget"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Month</label>
            <select
              value={month}
              onChange={e => setMonth(parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
            >
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Year</label>
            <input
              type="number"
              value={year}
              onChange={e => setYear(parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>
        </div>

        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 12 }}>Line Items</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Category</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', width: 200 }}>Budgeted Amount</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, idx) => (
              <tr key={idx} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px 12px' }}>
                  <input
                    value={item.category}
                    onChange={e => updateLineItem(idx, 'category', e.target.value)}
                    placeholder="Category name"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <input
                    type="number"
                    min="0"
                    value={item.budgeted_amount}
                    onChange={e => updateLineItem(idx, 'budgeted_amount', e.target.value)}
                    placeholder="0"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                  <button onClick={() => removeLineItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                    <XCircle size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          onClick={addCategory}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, border: '1px dashed #cbd5e1',
            background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer', marginBottom: 24
          }}
        >
          <Plus size={14} /> Add Category
        </button>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => handleCreate(false)}
            disabled={saving}
            style={{
              padding: '10px 20px', borderRadius: 10, border: '1px solid #cbd5e1',
              background: '#fff', color: '#475569', fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}
          >
            <Save size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={() => handleCreate(true)}
            disabled={saving}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}
          >
            <CheckCircle size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {saving ? 'Activating...' : 'Activate Budget'}
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== DETAIL VIEW ====================
  const renderDetail = () => {
    if (!detailData || !summaryData) {
      return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading budget details...</div>;
    }

    const health = getHealthBadge(summaryData.health);

    const getAdvisorSeverityColor = (sev) => {
      if (sev === 'danger') return { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' };
      if (sev === 'warning') return { bg: '#fef9c3', color: '#ca8a04', border: '#fde047' };
      return { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' };
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
              <ArrowLeft size={20} />
            </button>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1e293b' }}>
              {detailData.name}
            </h1>
            <span style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: health.bg, color: health.color,
            }}>
              {health.text}
            </span>
          </div>
          <button
            onClick={() => fetchAdvisor(selectedBudget?.id)}
            disabled={advisorLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #0d9488, #7c3aed)', color: '#fff',
              fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}
          >
            {advisorLoading ? 'Analyzing...' : 'Get Smart Advice'}
          </button>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Budgeted', value: fmtRs(detailData.total_budgeted), icon: Target, color: '#2563eb' },
            { label: 'Total Actual', value: fmtRs(detailData.total_actual), icon: BarChart2, color: '#7c3aed' },
            { label: 'Variance', value: (detailData.total_variance >= 0 ? '+' : '') + fmtRs(detailData.total_variance).replace('Rs. ', 'Rs. '), icon: detailData.total_variance >= 0 ? TrendingUp : TrendingDown, color: detailData.total_variance >= 0 ? '#dc2626' : '#16a34a' },
            { label: 'Variance %', value: `${(detailData.total_variance_percent || 0).toFixed(1)}%`, icon: AlertTriangle, color: '#ca8a04' },
          ].map((card, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{card.label}</span>
                <card.icon size={18} color={card.color} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          {/* Line Items Table */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 16 }}>Budget Line Items</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Category</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Budgeted</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Actual</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Variance</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Var %</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {detailData.line_items.map(item => {
                  const budgeted = item.budgeted_amount || 0;
                  const actual = item.actual_amount || 0;
                  const pct = budgeted ? (actual / budgeted) * 100 : 0;
                  return (
                    <tr key={item.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 12px', color: '#1e293b', fontWeight: 500 }}>{item.category}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1e293b' }}>{fmtRs(budgeted)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1e293b' }}>{fmtRs(actual)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: item.variance >= 0 ? '#dc2626' : '#16a34a', fontWeight: 500 }}>
                        {item.variance >= 0 ? '+' : ''}{fmtRs(item.variance)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#475569' }}>{(item.variance_percent || 0).toFixed(1)}%</td>
                      <td style={{ padding: '10px 12px' }}>
                        <StatusBar percent={pct} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Insights Panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Smart Budget Advisor */}
            {advisorData && (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BarChart2 size={14} /> Smart Budget Advisor
                </h4>
                <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>Based on your historical data</p>

                {!advisorData.ready ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                      Smart advice requires 6 months of data.
                    </p>
                    <p style={{ fontSize: 12, color: '#94a3b8' }}>
                      Import historical data to unlock this feature.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Overall Health Badge */}
                    <div style={{
                      display: 'inline-block', padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      marginBottom: 12,
                      background: advisorData.overall_assessment === 'Healthy' ? '#dcfce7' : advisorData.overall_assessment === 'Review Needed' ? '#fef9c3' : '#fee2e2',
                      color: advisorData.overall_assessment === 'Healthy' ? '#16a34a' : advisorData.overall_assessment === 'Review Needed' ? '#ca8a04' : '#dc2626',
                    }}>
                      {advisorData.overall_assessment}
                    </div>

                    {/* Projected Profit */}
                    <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Projected Profit</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: advisorData.projected_profit >= 0 ? '#16a34a' : '#dc2626' }}>
                        {fmtRs(advisorData.projected_profit)}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>
                        {advisorData.projected_profit_margin.toFixed(1)}% margin
                      </div>
                    </div>

                    {/* Line Item Advice Cards */}
                    {advisorData.line_item_advice?.map((advice, i) => {
                      const sev = getAdvisorSeverityColor(advice.severity);
                      return (
                        <div key={i} style={{
                          padding: 12, borderRadius: 8, marginBottom: 8,
                          background: sev.bg, border: `1px solid ${sev.border}`
                        }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: sev.color, marginBottom: 4 }}>{advice.category}</div>
                          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>{advice.advice_text}</p>
                        </div>
                      );
                    })}

                    {/* Top Recommendation */}
                    {advisorData.top_recommendation && (
                      <div style={{
                        padding: 14, borderRadius: 10, marginTop: 8,
                        background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)',
                        border: '1px solid #99f6e4'
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#0f766e', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Top Recommendation</div>
                        <p style={{ fontSize: 13, color: '#134e4a', lineHeight: 1.5 }}>{advisorData.top_recommendation}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Overspent */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={14} /> Overspent Categories
              </h4>
              {summaryData.overspent_categories.length === 0 ? (
                <p style={{ fontSize: 13, color: '#64748b' }}>None — great job!</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {summaryData.overspent_categories.map((cat, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#1e293b' }}>{cat.category}</span>
                      <span style={{ color: '#dc2626', fontWeight: 500 }}>+{fmtRs(cat.variance).replace('Rs. ', 'Rs. ')} ({cat.variance_percent.toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Underspent */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: '#16a34a', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingDown size={14} /> Underspent Categories
              </h4>
              {summaryData.underspent_categories.length === 0 ? (
                <p style={{ fontSize: 13, color: '#64748b' }}>None</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {summaryData.underspent_categories.map((cat, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#1e293b' }}>{cat.category}</span>
                      <span style={{ color: '#16a34a', fontWeight: 500 }}>{fmtRs(cat.variance)} ({cat.variance_percent.toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Best / Worst */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 12 }}>Performance</h4>
              {summaryData.best_category && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 500, marginBottom: 2 }}>Best Performing</div>
                  <div style={{ fontSize: 13, color: '#1e293b' }}>{summaryData.best_category.category}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{fmtRs(summaryData.best_category.variance)} under budget</div>
                </div>
              )}
              {summaryData.worst_category && (
                <div>
                  <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 500, marginBottom: 2 }}>Worst Performing</div>
                  <div style={{ fontSize: 13, color: '#1e293b' }}>{summaryData.worst_category.category}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{fmtRs(summaryData.worst_category.variance)} over budget</div>
                </div>
              )}
            </div>

            {/* Overall Health */}
            <div style={{ background: health.bg, borderRadius: 12, border: `1px solid ${health.color}30`, padding: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: health.color, marginBottom: 6 }}>Overall Budget Health</h4>
              <p style={{ fontSize: 13, color: health.color, lineHeight: 1.5 }}>
                {summaryData.health === 'On Track' && 'Your spending is well within budget. Keep it up!'}
                {summaryData.health === 'Warning' && 'You are approaching your budget limit. Consider reducing discretionary spending.'}
                {summaryData.health === 'Over Budget' && 'You have exceeded your budget. Review overspent categories immediately.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} user={user} />
      <div style={{ flex: 1, marginLeft: isSidebarOpen ? 256 : 0, transition: 'margin-left 0.3s' }}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main style={{ padding: 24 }}>
          {view === 'list' && renderList()}
          {view === 'create' && renderCreate()}
          {view === 'detail' && renderDetail()}
        </main>
      </div>
    </div>
  );
};

export default Budget;
